import type { PermissionDetail } from '@common/util/permissions'

/**
 * Frigade guide mode persists conversation history in memory and (via sessionStorage) across
 * remounts. The overlay title falls back to prior responses' `conversationTitle` when the current
 * guide response omits one — so a pricing-review guide can keep showing an earlier "available
 * units" title. These keys are Frigade's (see frigade-assistant.min.js); clearing them before each
 * independent Ask Rentana guide launch forces a fresh session title.
 */
export const FRIGADE_ASSISTANT_STATE_STORAGE_KEY = 'frigade-assistant-state'
export const FRIGADE_GUIDE_SESSION_ID_STORAGE_KEY = 'frigade-guide-session-id'

export type FrigadeGuideMountConfig = {
  apiKey: string
  apiUrl: string
  assistantId: string
  userId: string
  userProperties: {
    firstName: string
    lastName: string
    imageUrl?: string
    email: string
    role: string
    permissions: PermissionDetail[]
  }
  showWidget?: boolean
  mode?: 'guide' | 'assistant'
  preferences?: {
    prefersColorScheme?: 'light' | 'dark'
  }
  colorScheme?: {
    light?: { primaryColor?: string }
    dark?: { primaryColor?: string }
  }
  /** When set, Frigade starts a new session instead of restoring the guide-mode session id. */
  sessionIdOverride?: string
}

let frigadeGuideMountConfig: FrigadeGuideMountConfig | null = null

/** Remember the config used to mount the guide instance so we can remount with a fresh session. */
export function setFrigadeGuideMountConfig(config: FrigadeGuideMountConfig | null): void {
  frigadeGuideMountConfig = config
}

export function getFrigadeGuideMountConfig(): FrigadeGuideMountConfig | null {
  return frigadeGuideMountConfig
}

export function clearFrigadeGuideSessionStorage(): void {
  try {
    sessionStorage.removeItem(FRIGADE_ASSISTANT_STATE_STORAGE_KEY)
    sessionStorage.removeItem(FRIGADE_GUIDE_SESSION_ID_STORAGE_KEY)
  } catch {
    // sessionStorage can throw in private browsing / sandboxed iframes; ignore.
  }
}

/** Fresh session id for each independent Ask Rentana guide launch. */
export function createFrigadeGuideSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `frigade-guide-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * Clears Frigade's persisted guide history and remounts with a new session id so the next
 * `handleToolCall` / `launchGuide` cannot reuse a previous guide's title.
 *
 * Clears `handleToolCall` / `launchGuide` first so callers that then wait for handlers do not
 * invoke the previous (unmounted) instance's closures.
 */
export function remountFrigadeGuideSessionForNewLaunch(): void {
  clearFrigadeGuideSessionStorage()

  const mountConfig = frigadeGuideMountConfig
  const mountFrigade = window.FrigadeAssistant
  if (!mountConfig || typeof mountFrigade !== 'function') {
    return
  }

  mountFrigade.handleToolCall = undefined
  mountFrigade.launchGuide = undefined

  mountFrigade({
    ...mountConfig,
    sessionIdOverride: createFrigadeGuideSessionId(),
  })
}
